import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiVideo, FiMonitor, FiMessageSquare, FiSave, FiKey, FiCopy, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext.js';
import { toast } from 'react-hot-toast';

const StreamSettings = () => {
  const { currentUser } = useAuth();
  const [selectedTab, setSelectedTab] = useState('general');
  
  // Mock stream key (in a real app, this would come from a secure backend)
  const [streamKey, setStreamKey] = useState('xxxx-xxxx-xxxx-xxxx');
  const [showKey, setShowKey] = useState(false);
  
  // Mock stream settings
  const [settings, setSettings] = useState({
    quality: 'high',
    resolution: '1080p',
    bitrate: 6000,
    fps: 60,
    latencyMode: 'normal',
    chatEnabled: true,
    autoRecord: false,
    notifications: {
      followers: true,
      subscribers: true,
      donations: true
    }
  });
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setSettings(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };
  
  const saveSettings = () => {
    // In a real app, this would save to a database
    toast.success('Stream settings saved successfully!');
  };
  
  const regenerateStreamKey = () => {
    // In a real app, this would call a backend API
    const newKey = Math.random().toString(36).substring(2, 6) + '-' +
                  Math.random().toString(36).substring(2, 6) + '-' +
                  Math.random().toString(36).substring(2, 6) + '-' +
                  Math.random().toString(36).substring(2, 6);
    setStreamKey(newKey);
    toast.success('Stream key regenerated successfully!');
  };
  
  const copyStreamKey = () => {
    navigator.clipboard.writeText(streamKey);
    toast.success('Stream key copied to clipboard!');
  };
  
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-harmony-blue p-6">
        <div className="max-w-4xl mx-auto bg-harmony-blue-dark p-8 rounded-lg shadow-lg">
          <h1 className="text-2xl font-bold text-harmony-white mb-4">Stream Settings</h1>
          <p className="text-harmony-white/70">Please log in to access your stream settings.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-harmony-blue p-6">
      <div className="max-w-6xl mx-auto">
        <motion.h1 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-3xl font-bold text-harmony-white mb-6"
        >
          Stream Settings
        </motion.h1>
        
        {/* Settings Tabs */}
        <div className="flex border-b border-harmony-white/10 mb-6">
          <button
            className={`px-4 py-3 font-medium flex items-center ${
              selectedTab === 'general'
                ? 'text-harmony-orange border-b-2 border-harmony-orange'
                : 'text-harmony-white/70 hover:text-harmony-white'
            }`}
            onClick={() => setSelectedTab('general')}
          >
            <FiVideo className="mr-2" /> General
          </button>
          <button
            className={`px-4 py-3 font-medium flex items-center ${
              selectedTab === 'quality'
                ? 'text-harmony-orange border-b-2 border-harmony-orange'
                : 'text-harmony-white/70 hover:text-harmony-white'
            }`}
            onClick={() => setSelectedTab('quality')}
          >
            <FiMonitor className="mr-2" /> Quality
          </button>
          <button
            className={`px-4 py-3 font-medium flex items-center ${
              selectedTab === 'chat'
                ? 'text-harmony-orange border-b-2 border-harmony-orange'
                : 'text-harmony-white/70 hover:text-harmony-white'
            }`}
            onClick={() => setSelectedTab('chat')}
          >
            <FiMessageSquare className="mr-2" /> Chat
          </button>
          <button
            className={`px-4 py-3 font-medium flex items-center ${
              selectedTab === 'stream-key'
                ? 'text-harmony-orange border-b-2 border-harmony-orange'
                : 'text-harmony-white/70 hover:text-harmony-white'
            }`}
            onClick={() => setSelectedTab('stream-key')}
          >
            <FiKey className="mr-2" /> Stream Key
          </button>
        </div>
        
        {/* Settings Content */}
        <div className="bg-harmony-blue-dark rounded-lg p-6 border border-harmony-white/10 shadow-lg">
          {/* General Settings */}
          {selectedTab === 'general' && (
            <div>
              <h2 className="text-xl font-bold text-harmony-white mb-4">General Stream Settings</h2>
              <p className="text-harmony-white/70 mb-6">Configure basic stream preferences.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-harmony-white/70 mb-2">Latency Mode</label>
                  <select
                    name="latencyMode"
                    value={settings.latencyMode}
                    onChange={handleChange}
                    className="w-full bg-harmony-blue border border-harmony-white/10 text-harmony-white rounded px-3 py-2"
                  >
                    <option value="normal">Normal (15-30s)</option>
                    <option value="low">Low (5-10s)</option>
                    <option value="ultra-low">Ultra Low (2-5s)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-harmony-white/70 mb-2">Auto Record Streams</label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="autoRecord"
                      checked={settings.autoRecord}
                      onChange={handleChange}
                      className="mr-2 h-5 w-5"
                    />
                    <span className="text-harmony-white">Automatically record all streams</span>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <h3 className="text-lg font-medium text-harmony-white mb-3">Notifications</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="notifications.followers"
                      checked={settings.notifications.followers}
                      onChange={handleChange}
                      className="mr-2 h-5 w-5"
                    />
                    <span className="text-harmony-white">New Followers</span>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="notifications.subscribers"
                      checked={settings.notifications.subscribers}
                      onChange={handleChange}
                      className="mr-2 h-5 w-5"
                    />
                    <span className="text-harmony-white">New Subscribers</span>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="notifications.donations"
                      checked={settings.notifications.donations}
                      onChange={handleChange}
                      className="mr-2 h-5 w-5"
                    />
                    <span className="text-harmony-white">Donations</span>
                  </div>
                </div>
              </div>
              
              <button
                className="bg-harmony-orange text-harmony-white px-4 py-2 rounded flex items-center hover:bg-harmony-orange/90"
                onClick={saveSettings}
              >
                <FiSave className="mr-2" /> Save Settings
              </button>
            </div>
          )}
          
          {/* Quality Settings */}
          {selectedTab === 'quality' && (
            <div>
              <h2 className="text-xl font-bold text-harmony-white mb-4">Stream Quality Settings</h2>
              <p className="text-harmony-white/70 mb-6">Configure stream resolution, bitrate, and other quality settings.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-harmony-white/70 mb-2">Quality Preset</label>
                  <select
                    name="quality"
                    value={settings.quality}
                    onChange={handleChange}
                    className="w-full bg-harmony-blue border border-harmony-white/10 text-harmony-white rounded px-3 py-2"
                  >
                    <option value="high">High (1080p60, 6000kbps)</option>
                    <option value="medium">Medium (720p60, 4500kbps)</option>
                    <option value="low">Low (720p30, 2500kbps)</option>
                    <option value="mobile">Mobile (480p30, 1500kbps)</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-harmony-white/70 mb-2">Resolution</label>
                  <select
                    name="resolution"
                    value={settings.resolution}
                    onChange={handleChange}
                    className="w-full bg-harmony-blue border border-harmony-white/10 text-harmony-white rounded px-3 py-2"
                    disabled={settings.quality !== 'custom'}
                  >
                    <option value="1080p">1080p (1920x1080)</option>
                    <option value="720p">720p (1280x720)</option>
                    <option value="480p">480p (854x480)</option>
                    <option value="360p">360p (640x360)</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-harmony-white/70 mb-2">Bitrate (kbps)</label>
                  <select
                    name="bitrate"
                    value={settings.bitrate}
                    onChange={handleChange}
                    className="w-full bg-harmony-blue border border-harmony-white/10 text-harmony-white rounded px-3 py-2"
                    disabled={settings.quality !== 'custom'}
                  >
                    <option value={8000}>8000</option>
                    <option value={6000}>6000</option>
                    <option value={4500}>4500</option>
                    <option value={3500}>3500</option>
                    <option value={2500}>2500</option>
                    <option value={1500}>1500</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-harmony-white/70 mb-2">Framerate (FPS)</label>
                  <select
                    name="fps"
                    value={settings.fps}
                    onChange={handleChange}
                    className="w-full bg-harmony-blue border border-harmony-white/10 text-harmony-white rounded px-3 py-2"
                    disabled={settings.quality !== 'custom'}
                  >
                    <option value={60}>60</option>
                    <option value={30}>30</option>
                  </select>
                </div>
              </div>
              
              <button
                className="bg-harmony-orange text-harmony-white px-4 py-2 rounded flex items-center hover:bg-harmony-orange/90"
                onClick={saveSettings}
              >
                <FiSave className="mr-2" /> Save Settings
              </button>
            </div>
          )}
          
          {/* Chat Settings */}
          {selectedTab === 'chat' && (
            <div>
              <h2 className="text-xl font-bold text-harmony-white mb-4">Chat Settings</h2>
              <p className="text-harmony-white/70 mb-6">Configure chat preferences for your stream.</p>
              
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    name="chatEnabled"
                    checked={settings.chatEnabled}
                    onChange={handleChange}
                    className="mr-2 h-5 w-5"
                  />
                  <span className="text-harmony-white">Enable chat for streams</span>
                </div>
                
                <div className="mb-6">
                  <p className="text-harmony-white/70 mb-2">
                    When disabled, viewers won't be able to send chat messages during your streams.
                  </p>
                </div>
                
                <h3 className="text-lg font-medium text-harmony-white mb-3">Chat Moderation</h3>
                <p className="text-harmony-white/70 mb-6">
                  Additional chat moderation options coming soon.
                </p>
              </div>
              
              <button
                className="bg-harmony-orange text-harmony-white px-4 py-2 rounded flex items-center hover:bg-harmony-orange/90"
                onClick={saveSettings}
              >
                <FiSave className="mr-2" /> Save Settings
              </button>
            </div>
          )}
          
          {/* Stream Key Settings */}
          {selectedTab === 'stream-key' && (
            <div>
              <h2 className="text-xl font-bold text-harmony-white mb-4">Stream Key</h2>
              <p className="text-harmony-white/70 mb-6">
                Your stream key is used to authenticate your broadcasting software with our platform.
                Keep this key private - anyone with your stream key can stream to your channel.
              </p>
              
              <div className="bg-harmony-blue rounded-lg p-4 border border-harmony-white/10 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 font-mono">
                    {showKey ? streamKey : '••••-••••-••••-••••'}
                  </div>
                  <div className="flex">
                    <button
                      className="text-harmony-white/70 hover:text-harmony-white p-2"
                      onClick={() => setShowKey(!showKey)}
                    >
                      {showKey ? 'Hide' : 'Show'}
                    </button>
                    <button
                      className="text-harmony-white/70 hover:text-harmony-white p-2"
                      onClick={copyStreamKey}
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center mb-6">
                <button
                  className="bg-red-500 text-white px-4 py-2 rounded flex items-center hover:bg-red-600"
                  onClick={regenerateStreamKey}
                >
                  <FiRefreshCw className="mr-2" /> Reset Stream Key
                </button>
                <p className="ml-4 text-harmony-white/70 text-sm">
                  Warning: This will immediately invalidate your existing stream key.
                  You'll need to update your broadcasting software with the new key.
                </p>
              </div>
              
              <h3 className="text-lg font-medium text-harmony-white mb-3">Stream URL</h3>
              <div className="bg-harmony-blue rounded-lg p-4 border border-harmony-white/10 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 font-mono">
                    rtmp://stream.streamerwars.com/live
                  </div>
                  <div>
                    <button
                      className="text-harmony-white/70 hover:text-harmony-white p-2"
                      onClick={() => {
                        navigator.clipboard.writeText('rtmp://stream.streamerwars.com/live');
                        toast.success('Stream URL copied to clipboard!');
                      }}
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-medium text-harmony-white mb-3">Streaming Software Setup</h3>
              <p className="text-harmony-white/70 mb-3">
                To connect your streaming software (OBS, Streamlabs, XSplit, etc.), use the following settings:
              </p>
              <ul className="list-disc pl-5 mb-6 text-harmony-white/70">
                <li className="mb-2">Stream URL: rtmp://stream.streamerwars.com/live</li>
                <li className="mb-2">Stream Key: Your stream key from above</li>
                <li className="mb-2">For optimal quality, configure your software to match your quality settings</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamSettings; 